import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, Link as LinkIcon, Palette, Type, RotateCcw } from 'lucide-react';
import { sanitizeRichHtml } from '../lib/sanitize';

/**
 * Lightweight WYSIWYG editor built on contentEditable + document.execCommand.
 * Intentionally dependency-free — the HTML output is suitable for embedding
 * in SMTP emails (where modern rich-text editors often produce CSS that email
 * clients strip).
 *
 * Supports : bold/italic/underline, font size (1-7), font family, foreground
 * colour, alignment, ordered/unordered lists, hyperlink, undo.
 */

const FONT_FAMILIES = [
  { label: 'Sans-serif', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Serif', value: 'Georgia, Times, serif' },
  { label: 'Monospace', value: 'Menlo, Consolas, monospace' },
  { label: 'Calibri', value: 'Calibri, sans-serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
];

// execCommand font sizes are 1..7 (deprecated but works in every browser)
const FONT_SIZES = [
  { label: 'XS', value: '1' },
  { label: 'S', value: '2' },
  { label: 'Normal', value: '3' },
  { label: 'L', value: '4' },
  { label: 'XL', value: '5' },
  { label: 'XXL', value: '6' },
];

export default function RichTextEditor({ value, onChange, placeholder, testId }) {
  const editorRef = useRef(null);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  // Guard against echo-updates: only reset content when `value` changes externally
  // (not when the user types). We compare with the editor's own HTML.
  // Vague 96.12 — also re-sync once on mount AFTER first paint, in case the
  // parent passes the value via async loadAll(): the very first useEffect run
  // happens with editorRef.current set, but if React batched the state update
  // weirdly we want a second tick.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const incoming = sanitizeRichHtml(value || '');
    if (incoming !== el.innerHTML) {
      el.innerHTML = incoming;
    }
  }, [value]);

  const exec = useCallback((command, arg) => {
    // Keep the selection inside the editor before applying the command
    editorRef.current?.focus();
    try { document.execCommand(command, false, arg); } catch { /* noop */ }
    onChange?.(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const onInput = () => {
    onChange?.(editorRef.current?.innerHTML || '');
  };

  const handleLink = () => {
    const url = window.prompt('URL du lien :', 'https://');
    if (url) exec('createLink', url);
  };

  return (
    <div className="rounded-xl border border-[#EFF0F6] bg-white overflow-hidden" data-testid={testId}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-[#EFF0F6] bg-[#FAFAFE]">
        <select
          data-testid="sig-editor-font-family"
          onChange={(e) => exec('fontName', e.target.value)}
          defaultValue=""
          className="h-7 px-2 rounded-md bg-white border border-[#EFF0F6] text-[11.5px] text-[#4A4B63]"
          title="Police"
        >
          <option value="" disabled>Police</option>
          {FONT_FAMILIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        <select
          data-testid="sig-editor-font-size"
          onChange={(e) => exec('fontSize', e.target.value)}
          defaultValue=""
          className="h-7 px-2 rounded-md bg-white border border-[#EFF0F6] text-[11.5px] text-[#4A4B63]"
          title="Taille"
        >
          <option value="" disabled>Taille</option>
          {FONT_SIZES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <div className="w-px h-5 bg-[#EFF0F6] mx-1" />

        <ToolBtn icon={Bold} onClick={() => exec('bold')} title="Gras" testId="sig-editor-bold" />
        <ToolBtn icon={Italic} onClick={() => exec('italic')} title="Italique" testId="sig-editor-italic" />
        <ToolBtn icon={Underline} onClick={() => exec('underline')} title="Souligné" testId="sig-editor-underline" />

        <div className="w-px h-5 bg-[#EFF0F6] mx-1" />

        <div className="relative">
          <ToolBtn icon={Palette} onClick={() => setColorPickerOpen((v) => !v)} title="Couleur" testId="sig-editor-color" />
          {colorPickerOpen && (
            <div className="absolute z-10 top-8 left-0 p-2 rounded-lg bg-white border border-[#EFF0F6] shadow-lg grid grid-cols-6 gap-1" data-testid="sig-editor-color-palette">
              {['#1A1B2E', '#4A4B63', '#4F46E5', '#14B8A6', '#F59E0B', '#EF4444', '#0EA5E9', '#10B981', '#EC4899', '#6366F1', '#FFFFFF', '#000000'].map((c) => (
                <button
                  key={c}
                  type="button"
                  className="w-5 h-5 rounded border border-[#EFF0F6] hover:scale-110 transition-transform"
                  style={{ background: c }}
                  onClick={() => { exec('foreColor', c); setColorPickerOpen(false); }}
                  aria-label={`Couleur ${c}`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="w-px h-5 bg-[#EFF0F6] mx-1" />

        <ToolBtn icon={AlignLeft} onClick={() => exec('justifyLeft')} title="Aligner à gauche" />
        <ToolBtn icon={AlignCenter} onClick={() => exec('justifyCenter')} title="Centrer" />
        <ToolBtn icon={AlignRight} onClick={() => exec('justifyRight')} title="Aligner à droite" />

        <div className="w-px h-5 bg-[#EFF0F6] mx-1" />

        <ToolBtn icon={LinkIcon} onClick={handleLink} title="Lien" />
        <ToolBtn icon={RotateCcw} onClick={() => exec('undo')} title="Annuler" />
      </div>

      <div
        ref={editorRef}
        contentEditable
        data-testid={`${testId || 'rich-editor'}-area`}
        onInput={onInput}
        onBlur={onInput}
        suppressContentEditableWarning
        className="min-h-[140px] max-h-[280px] overflow-y-auto px-3 py-2 text-[13.5px] text-[#1A1B2E] outline-none rich-editor"
        data-placeholder={placeholder || 'Rédigez votre signature ici…'}
      />
      <style>{`
        .rich-editor:empty::before {
          content: attr(data-placeholder);
          color: #8E8FA5;
          pointer-events: none;
        }
        .rich-editor a { color: #4F46E5; text-decoration: underline; }
        .rich-editor b, .rich-editor strong { font-weight: 700; }
      `}</style>
    </div>
  );
}

function ToolBtn({ icon: Icon, onClick, title, testId }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
      data-testid={testId}
      className="h-7 w-7 rounded-md hover:bg-[#EEF2FF] flex items-center justify-center text-[#4A4B63] hover:text-[#4F46E5] transition-colors"
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}
